/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { XdpV1_Org_Coursera_Degree_Stored_Api_UpsellType as UpsellTypes } from '__generated__/graphql-types';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import { Divider, breakpoints } from '@coursera/cds-core';

import type {
  UniversityProgramDegreeDescriptionPageAdmissionsProcessLinks as AdmissionsProcessLinks,
  UniversityProgramDegreeDescriptionPageTuitionGridLinksLinks as GridLinksItemsType,
  UniversityProgramDeadlines,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import AdmissionsProcessModule from 'bundles/degree-description/components/modules/admissions-process/AdmissionsProcessModule';
import CareerModule from 'bundles/degree-description/components/modules/career-section/CareerModule';
import DeadlineCard from 'bundles/degree-description/components/modules/deadline/DeadlineCard';
import DeadlineModule from 'bundles/degree-description/components/modules/deadline/DeadlineModule';
import EventsSection from 'bundles/degree-description/components/modules/events/EventsSection';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import FaqModule from 'bundles/degree-description/components/modules/faqs/FaqModule';
import HelpCenterBlock from 'bundles/degree-description/components/modules/faqs/HelpCenterBlock';
import FCNDModule from 'bundles/degree-description/components/modules/fcnd/FCNDModule';
import FinanceSectionModule from 'bundles/degree-description/components/modules/finance/FinanceSectionModule';
import GridLinksModule from 'bundles/degree-description/components/modules/grid-links/GridLinksModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiAtAGlanceBlock from 'bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import InstructorSectionModule from 'bundles/degree-description/components/modules/instructor-section/InstructorSectionModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import RelatedNewsModule from 'bundles/degree-description/components/modules/related-news/RelatedNewsModule';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import Testimonials from 'bundles/degree-description/components/modules/testimonials/TestimonialsModule';
import UpsellsAboutModule from 'bundles/degree-description/components/modules/upsells/UpsellsAboutModule';
import UpsellsModule from 'bundles/degree-description/components/modules/upsells/UpsellsModule';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import Container from 'bundles/degree-description/components/shared/Container';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionUpsellsData } from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import {
  LIGHTWEIGHT_NAVIGATION_ITEMS,
  filterAndSliceStackableItemsByUpsellType,
} from 'bundles/degree-description/utils/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import getDegreesConversionCopy from 'bundles/stackability-common/copy/degreesConversionCopy';

import _t from 'i18n!nls/degree-description';

type Props = PropsFromDegreePageContent;

// Try to keep styling within modules and avoid adding styles to subpages as much as possible
const styles = {
  mobileOverviewAbout: css`
    padding: var(--cds-spacing-200);
  `,
  admissionsPadding: css`
    padding-top: var(--cds-spacing-400);
  `,
  navigationScroll: css`
    scroll-margin-top: 60px;
    padding-top: var(--cds-spacing-800);
    ${breakpoints.down('sm')} {
      padding-top: 0;
    }
  `,
};

const LightweightPageContent = ({
  pageContent,
  eoiContainerText,
  eoiButtons,
  setEoiTrackingIndex,
  personalizedDdpData,
}: Props) => {
  const {
    overviewAbout,
    backgroundImage,
    partner,
    programName,
    atAGlance,
    faqCollection,
    careerSection,
    latestNews,
    upcomingEventsCollection,
    testimonialsCollection,
    financeSection,
    upsellsAbout,
    deadlineSectionCollection,
    tuitionGridLinks,
    admissionsAbout,
    admissionsProcess,
    academicBlocksCollection,
    instructorsSection,
  } = pageContent;

  const { stackableItemsWithUpsellType } = useDegreeDescriptionUpsellsData();

  const partOfUpsells = filterAndSliceStackableItemsByUpsellType(stackableItemsWithUpsellType, UpsellTypes.PartOf);
  const offersCreditUpsells = filterAndSliceStackableItemsByUpsellType(
    stackableItemsWithUpsellType,
    UpsellTypes.OffersCredit
  );
  const relatedToUpsells = filterAndSliceStackableItemsByUpsellType(
    stackableItemsWithUpsellType,
    UpsellTypes.RelatedTo
  );

  const offersCreditUpsellsExist = offersCreditUpsells && offersCreditUpsells.length > 0;
  const partOfUpsellsExist = partOfUpsells && partOfUpsells.length > 0;
  const relatedToUpsellsExist = relatedToUpsells && relatedToUpsells.length > 0;
  const upsellsExist = offersCreditUpsellsExist || partOfUpsellsExist || relatedToUpsellsExist;

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  const upsellsModuleCopy = getDegreesConversionCopy().stackabilityModuleRefresh.upsellsModuleCopy;
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const showFCNDModule = getDegreesConversionCopy().stackabilityModuleRefresh.fcndModuleCopy(slug).ctaLink;
  const showUpsellsAboutModule = !showFCNDModule;

  return (
    <div>
      <SchemaMarkup pageName={PAGE_NAMES_MARKUP.overview} pathName={location.pathname} pageContent={pageContent} />
      <div id="overview">
        <HeroModule
          informationalSection={
            <HideOnMobile>
              <InformationalSectionContainer
                content={
                  <RichText
                    content={overviewAbout?.json}
                    contentLinks={overviewAbout?.links}
                    linkTrackingName="overviewAbout_body_link"
                    buttonTrackingNameV3="degree_info_link"
                  />
                }
                sidebarContent={
                  <>
                    <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
                  </>
                }
              />
            </HideOnMobile>
          }
          heroBanner={
            <HeroBanner
              backgroundImageUrl={backgroundImage?.url ?? ''}
              partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
              partnerName={partner?.partnerName ?? ''}
              programName={programName ?? ''}
              isOverviewPage
              sidebarContent={
                <EoiButtonsBlock
                  eoiContainerText={eoiContainerText}
                  eoiButtons={eoiButtons}
                  trackingIndex={1}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              }
            />
          }
          contentInBetween={
            <>
              <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
              <InlineNavigation
                navigationItems={LIGHTWEIGHT_NAVIGATION_ITEMS}
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                parentNavRoute={routes[routes.length - 2].name}
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                currentNavRoute={routes[routes.length - 1].name}
                // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                slug={slug}
                isLightweightDdp
              />
            </>
          }
        />

        <HideOnDesktop>
          <>
            <EoiAtAGlanceBlock
              mobileOnly
              atAGlance={atAGlance}
              eoiContainerText={eoiContainerText}
              eoiButtons={eoiButtons}
              setEoiTrackingIndex={setEoiTrackingIndex}
            />
            <div css={styles.mobileOverviewAbout}>
              <RichText
                content={overviewAbout?.json}
                contentLinks={overviewAbout?.links}
                linkTrackingName="overviewAbout_body_link"
                buttonTrackingNameV3="degree_info_link"
              />
            </div>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
            <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
          </>
        </HideOnDesktop>

        <HideOnMobile>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
          </>
        </HideOnMobile>
      </div>
      <div id="tuition-financing" css={styles.navigationScroll}>
        {financeSection && (
          <ModuleContainerStyleWrap>
            <FinanceSectionModule
              title={financeSection?.title || ''}
              markdownContent={financeSection?.description || ''}
              ctaLink={financeSection?.callToActionLink || ''}
              ctaText={financeSection?.callToActionLabel || ''}
            />
          </ModuleContainerStyleWrap>
        )}
        {tuitionGridLinks && (
          <ModuleContainerStyleWrap>
            <GridLinksModule
              gridLinkText={tuitionGridLinks?.json}
              gridLinkItems={tuitionGridLinks?.links as GridLinksItemsType}
            />
          </ModuleContainerStyleWrap>
        )}
      </div>

      <div id="careers" css={styles.navigationScroll}>
        {testimonialsCollection && (
          <Testimonials
            testimonials={testimonialsCollection}
            applicationUrl={pageContent?.expressionOfInterest?.applyButtonUrl}
            eoiButtons={eoiButtons}
            // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            slug={slug}
          />
        )}

        {careerSection && (
          <ModuleContainerStyleWrap>
            <CareerModule careerSection={careerSection} />
          </ModuleContainerStyleWrap>
        )}
      </div>

      <HideOnMobile>
        <EoiInlineModule
          eoiContainerText={eoiContainerText}
          eoiButtons={eoiButtons}
          setEoiTrackingIndex={setEoiTrackingIndex}
        />
      </HideOnMobile>

      <div id="admissions" css={styles.navigationScroll}>
        {(admissionsAbout?.json || admissionsProcess) && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            {admissionsAbout?.json && (
              <Container css={styles.admissionsPadding}>
                <RichText
                  content={admissionsAbout?.json}
                  contentLinks={admissionsAbout?.links}
                  linkTrackingName="admissionsAbout_body_link"
                  buttonTrackingNameV3="degree_info_link"
                />
                {deadlineSectionCollection?.items && deadlineSectionCollection?.items.length > 0 && (
                  <DeadlineModule
                    deadlineItems={deadlineSectionCollection.items.map((deadline) => (
                      <DeadlineCard
                        key={`degree-deadline-card-${deadline?.sys?.id}`}
                        title={deadline?.title}
                        enrollApplyStart={deadline?.enrollApplyStart}
                        enrollApplyEnd={deadline?.enrollApplyEnd}
                        coursesStart={deadline?.coursesStart}
                        deadlineType={deadline?.deadlineType}
                        enrollApplyCustomText={deadline?.enrollApplyCustomText}
                        coursesStartCustomText={deadline?.coursesStartCustomText}
                      />
                    ))}
                  />
                )}
              </Container>
            )}

            {admissionsProcess && (
              <AdmissionsProcessModule
                content={admissionsProcess.json}
                links={admissionsProcess.links as AdmissionsProcessLinks}
              />
            )}
          </ModuleContainerStyleWrap>
        )}
      </div>

      <HideOnMobile>
        <EoiInlineModule
          eoiContainerText={eoiContainerText}
          eoiButtons={eoiButtons}
          setEoiTrackingIndex={setEoiTrackingIndex}
        />
      </HideOnMobile>

      <div id="academics" css={styles.navigationScroll}>
        {academicBlocksCollection?.items && academicBlocksCollection?.items.length > 0 && (
          <ModuleContainerStyleWrap>
            <FaqModule
              header={_t('Curriculum')}
              faqItems={academicBlocksCollection.items}
              accordionTrackingName="curriculum"
              accordionEventingV3TrackingName="admission_item"
              linkTrackingName="curriculum_link"
            />
          </ModuleContainerStyleWrap>
        )}

        {upsellsExist && showFCNDModule && (
          <ModuleContainerStyleWrap>
            <FCNDModule />
          </ModuleContainerStyleWrap>
        )}

        {upsellsExist &&
          showUpsellsAboutModule &&
          upsellsAbout?.visual &&
          upsellsAbout.visual?.url &&
          upsellsAbout.visual?.width &&
          upsellsAbout.visual?.height && (
            <ModuleContainerStyleWrap>
              <UpsellsAboutModule
                title={upsellsAbout?.title || ''}
                description={upsellsAbout?.description || ''}
                ctaLabel={upsellsAbout?.callToActionLabel || ''}
                ctaLink={upsellsAbout?.callToActionLink || ''}
                image={
                  <LazyImgix
                    src={upsellsAbout.visual.url}
                    maxWidth={upsellsAbout.visual.width}
                    maxHeight={upsellsAbout.visual.height}
                    alt={upsellsAbout.visual.title || ''}
                    enableFluidWidth={true}
                  />
                }
              />
            </ModuleContainerStyleWrap>
          )}

        {upsellsExist && (
          <ModuleContainerStyleWrap>
            {offersCreditUpsellsExist && (
              <UpsellsModule
                title={upsellsModuleCopy.offersCredit.title}
                description={upsellsModuleCopy.offersCredit.description}
                disclaimer={upsellsModuleCopy.offersCredit.disclaimer}
                upsells={offersCreditUpsells}
                trackingNamePrefix="offers_credit"
              />
            )}

            {partOfUpsellsExist && (
              <>
                {offersCreditUpsellsExist && <Divider />}

                <UpsellsModule
                  title={upsellsModuleCopy.partOf.title}
                  description={upsellsModuleCopy.partOf.description}
                  upsells={partOfUpsells}
                  trackingNamePrefix="part_of"
                />
              </>
            )}
          </ModuleContainerStyleWrap>
        )}
      </div>

      <div id="student-experience" css={styles.navigationScroll}>
        {instructorsSection && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            <InstructorSectionModule
              title={_t('Meet your instructors')}
              json={instructorsSection?.json}
              instructorItems={instructorsSection?.links.entries.block as []}
            />
          </ModuleContainerStyleWrap>
        )}

        {upcomingEventsCollection && !!upcomingEventsCollection.items.length && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            <EventsSection events={upcomingEventsCollection?.items} />
          </ModuleContainerStyleWrap>
        )}

        {latestNews && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            <RelatedNewsModule relatedNewsContent={latestNews} />
          </ModuleContainerStyleWrap>
        )}

        {relatedToUpsellsExist && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            <UpsellsModule
              title={upsellsModuleCopy.relatedTo.title}
              description={upsellsModuleCopy.relatedTo.description}
              upsells={relatedToUpsells}
              trackingNamePrefix="related_to"
            />
          </ModuleContainerStyleWrap>
        )}

        {faqCollection?.items && faqCollection.items.length > 0 && (
          <ModuleContainerStyleWrap whiteBackgroundFirst>
            <FaqModule
              header={_t('Frequently asked questions')}
              faqItems={faqCollection.items}
              sideColumnContent={<HelpCenterBlock />}
            />
          </ModuleContainerStyleWrap>
        )}
      </div>

      <HideOnMobile>
        <EoiInlineModule
          eoiContainerText={eoiContainerText}
          eoiButtons={eoiButtons}
          setEoiTrackingIndex={setEoiTrackingIndex}
        />
      </HideOnMobile>
    </div>
  );
};

export default LightweightPageContent;
